import { useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import { Link } from "../router.tsx";

/**
 * A grouped settings page's sub-navigation (every settings screen):
 * link tabs, the current one marked `aria-current="page"`, each with a quiet count when
 * non-zero (worded for screen readers by `countLabel`).
 */
export function SettingsGroupTabs<G extends string>(props: {
  label: string;
  groups: readonly G[];
  current: G;
  href: (group: G) => string;
  groupLabel: (group: G) => string;
  count: (group: G) => number;
  countLabel: (group: G, count: number) => string;
}) {
  const list = useRef<HTMLUListElement>(null);
  // On a narrow page the tabs scroll sideways: the current one stays in view, also when its
  // label widens (the web font loads, the set-here count arrives).
  useEffect(() => {
    const strip = list.current;
    const current = strip?.querySelector<HTMLElement>(`[data-group='${props.current}']`);
    if (!strip || !current) {
      return;
    }
    const show = () => {
      const left = current.offsetLeft - strip.offsetLeft;
      if (left < strip.scrollLeft) {
        strip.scrollLeft = left;
      } else if (left + current.offsetWidth > strip.scrollLeft + strip.clientWidth) {
        strip.scrollLeft = left + current.offsetWidth - strip.clientWidth;
      }
    };
    show();
    if (typeof ResizeObserver === "undefined") {
      return;
    }
    const observer = new ResizeObserver(show);
    observer.observe(current);
    // The strip narrows too (a resized window): the current tab must stay in view.
    observer.observe(strip);
    return () => observer.disconnect();
  }, [props.current]);
  return (
    <nav aria-label={props.label} className="border-b border-line">
      <ul ref={list} className="-mb-px flex gap-5 overflow-x-auto">
        {props.groups.map((g) => {
          const current = g === props.current;
          const count = props.count(g);
          return (
            <li key={g}>
              <Link
                href={props.href(g)}
                data-group={g}
                aria-current={current ? "page" : undefined}
                className={cn(
                  "inline-flex h-10 items-center border-b-2 text-sm font-medium whitespace-nowrap transition-colors duration-(--duration-fast)",
                  current
                    ? "border-fg text-fg"
                    : "border-transparent text-fg-secondary hover:text-fg",
                )}
              >
                {props.groupLabel(g)}
                {count ? (
                  <>
                    <span
                      aria-hidden="true"
                      className="ml-1.5 inline-flex h-4.5 min-w-4.5 items-center justify-center rounded-full bg-surface-selected px-1 text-[11px] font-semibold text-fg-secondary tabular-nums"
                    >
                      {count}
                    </span>
                    <span className="sr-only">, {props.countLabel(g, count)}</span>
                  </>
                ) : null}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
